import {
  ChatStatus,
  type Message,
  type Thread,
  chatHistoryToMessages,
  getSDK,
  hasSDK,
} from '@littlebird/sdk';
import { useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { backendApis } from '@/backendApis';
import { logError } from '@/lib/errorLogging';
import { logger } from '@/lib/logger';
import { Route } from '@/routes/shared-chat.$id';
import { userStore } from '@/stores/user.store';

import { type SharedChatError } from './shared-chat.types';

export const useSharedChat = () => {
  const { id } = useParams({ strict: false });
  const { publicToken } = Route.useSearch();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<SharedChatError | null>(null);
  const [thread, setThread] = useState<Thread>();
  const [messages, setMessages] = useState<Message[]>();

  useEffect(() => {
    async function getMessages() {
      try {
        setIsLoading(true);
        if (!publicToken || !id) return;
        const chat = await backendApis.chat.getSharedChat(
          id,
          publicToken,
          userStore.isAuthenticated
        );
        const parsed = await convertChatHistoryToMessages(
          chat.chat_history,
          chat.chat_id,
          publicToken
        );
        setMessages(parsed);
        setThread({
          title: chat.title,
          starred: chat.starred,
          id: chat.chat_id,
          status: ChatStatus.Done,
        } as unknown as Thread);

        setIsLoading(false);
      } catch (error) {
        logError(error, 'Failed to fetch shared chats');

        const e = error as unknown as {
          status?: number;
          body?: {
            detail?: {
              error_code?: string;
              user_error_message: string;
            };
          };
        };

        const message =
          e.body?.detail?.user_error_message ??
          'The shared chat link appears to be invalid or expired.';
        const errorCode = e.body?.detail?.error_code;
        setError({
          statusCode: e.status ?? 403,
          title:
            errorCode === 'login_required'
              ? 'Sign in to view this chat'
              : errorCode === 'access_restricted'
                ? 'Access restricted'
                : 'Something went wrong',
          message: [message],
        });
      } finally {
        setIsLoading(false);
      }
    }
    getMessages();
  }, [id, publicToken]);

  return {
    publicToken,
    id,
    isLoading,
    error,
    thread,
    messages,
  };
};

export async function convertChatHistoryToMessages(
  chat_history: string[][],
  chat_id: string,
  publicToken?: string
) {
  const parsed = chatHistoryToMessages(chat_history, chat_id, logger);

  if (hasSDK() && publicToken) {
    const sdk = getSDK();
    const allFileIds = new Set(parsed.flatMap((msg) => msg.fileIds ?? []));

    await Promise.all(
      [...allFileIds].map(async (fileId) => {
        try {
          const fetchSharedFileMetadata = () =>
            backendApis.chat.getSharedChatFile(
              chat_id,
              fileId,
              publicToken,
              userStore.isAuthenticated
            );
          const metadata = await fetchSharedFileMetadata();
          sdk.stores.files.addFile({
            id: fileId,
            filename: metadata.filename,
            contentType: metadata.content_type,
            size: metadata.size,
            width: metadata.width ?? undefined,
            height: metadata.height ?? undefined,
          });
          // A share viewer may not be logged in, so expired URLs renew
          // through the public-token endpoint, not the authenticated one
          sdk.stores.files.registerFileUrl(fileId, {
            url: metadata.url,
            expiresIn: metadata.expires_in,
            refresh: async () => {
              const renewed = await fetchSharedFileMetadata();
              return { url: renewed.url, expiresIn: renewed.expires_in };
            },
          });
        } catch (error) {
          logger.error(`Failed to fetch shared chat file ${fileId}`, {
            metadata: { error },
            context: 'SharedChat:convertChatHistoryToMessages',
          });
        }
      })
    );
  }

  return parsed;
}
